import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { OverlayTrigger } from "react-bootstrap"

import { IDLE, PENDING, ARCHIVED, ACHIEVED } from "const"
import { OkrKeyResultUpdate } from "server/models"
import { StateType, deepCopy } from "client/utils"
import {
    StoreType,
    OkrKeyResultObjectProps,
    OkrKeyResultList,

    DeleteOkrKeyResultUpdate,
    LoadOkrKeyResultUpdates,
    UpdateOkrKeyResultActualNumber,
    LoadOkrKeyResultById,

    setOkrKeyResultUpdateList,
    setOkrKeyResultList,
    addModal,
} from "client/store"
import {
    UserIcon,
    DateFormatter,
    DateFormatterString,
    ClockIcon,
    ClockThinIcon,
    BookIcon,
    EllipsisIcon,
    SpinnerIcon,
    Button,

    PopupMenu,
    PopupMenuItem,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmBottom,
} from "client/components"
import { Confidence, ProgressPercents } from "client/pages/okr/styled"
import {
    KeyResultUpdateListWrapper,

    UpdateListWrapper,
    UpdateWrapper,
    UpdateTitleWrapper,
    UpdateTitle,
    UpdateTitleConfidence,
    UpdateTitlePercents,

    UpdateBody,
    UpdateBodyDescriptionWrapper,
    UpdateBodyDescription,
    UpdateBodyPercents,
    UpdateBodyOptions,
    UpdateBodyNote,

    EmptyUpdateListWrapper,
    EmptyClockWrapper,
    EmptyUpdateListTitle,
    EmptyUpdateListSubtitle,
    EmptyUpdateListTextButton,

    TooltipStyled,
    ClockWrapper,
    OptionsButton,
    PercentsChange,
} from "./styled"

import KeyResultUpdateEdit from "./edit"

const KeyResultUpdates = ({
    keyResult,
}: OkrKeyResultObjectProps) => {
    const updateList = useSelector((state: StoreType) => state.okrKeyResultUpdate.updateList)
    const keyResultList = useSelector((state: StoreType) => state.okrKeyResult.keyResultList)
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const metricList = useSelector((state: StoreType) => state.okrMetric.metricList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeUpdate, setActiveUpdate] = useState<OkrKeyResultUpdate>()

    const [showPopup, setShowPopup] = useState(false)
    const [popupTarget, setPopupTarget] = useState<HTMLElement>(null)

    const [showConfirmDelete, setShowConfirmDelete] = useState(false)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)

    const hideMenu = () => {
        setShowPopup(false)
    }

    useEffect(() => {
        document.addEventListener("click", hideMenu)
        return () => {
            document.removeEventListener("click", hideMenu)
        }
    }, [])

    const onEditUpdate = () => {
        dispatch(addModal({
            key: "editUpdate",
            content: <KeyResultUpdateEdit updateId={activeUpdate.id} keyResult={keyResult} />,
        }))
    }

    const onCancelDelete = () => {
        setShowConfirmDelete(false)
    }

    const onConfirmDelete = () => {
        setDeleteState(PENDING)
        DeleteOkrKeyResultUpdate(activeUpdate.id, keyResult.id).then(() => {
            // reload update list
            LoadOkrKeyResultUpdates(keyResult).then((res) => {
                dispatch(setOkrKeyResultUpdateList({ [keyResult.id]: res }))

                // update key result active value
                const lastUpdate = res[res.length - 1]
                UpdateOkrKeyResultActualNumber({
                    id: keyResult.id,
                    actualNumber: lastUpdate ? lastUpdate.value : keyResult.initialNumber,
                }).then(() => {
                    LoadOkrKeyResultById(keyResult.id, keyResult.objectiveId, keyResult.sessionId)
                    .then((updatedKeyResult) => {
                        const keyResultListCopy: OkrKeyResultList = deepCopy(keyResultList)
                        keyResultListCopy[keyResult.id] = updatedKeyResult
                        dispatch(setOkrKeyResultList(keyResultListCopy))

                        setDeleteState(IDLE)
                        setShowConfirmDelete(false)
                        setActiveUpdate(null)
                    })
                })
            })
        })
    }

    const isArchivedSession = session.status === ARCHIVED

    const metric = metricList[keyResult.metricId] || null
    const metricBefore = metric && !metric.displayAfter ? metric.symbol : ""
    const metricAfter = metric && metric.displayAfter ? metric.symbol : ""

    const updates = updateList[keyResult.id] || []

    return (
        <KeyResultUpdateListWrapper>
            {
                !updates.length && (
                    <EmptyUpdateListWrapper>
                        <EmptyClockWrapper><ClockThinIcon /></EmptyClockWrapper>
                        <EmptyUpdateListTitle>{lang.keyResultNeverUpdated}</EmptyUpdateListTitle>
                        {
                            !isArchivedSession && ([
                                <EmptyUpdateListSubtitle key="subtitle">
                                    {lang.keyResultNeverUpdatedDescription}
                                </EmptyUpdateListSubtitle>,
                                <EmptyUpdateListTextButton
                                    key="button"
                                    onClick={() => dispatch(addModal({
                                        key: "addUpdate",
                                        content: <KeyResultUpdateEdit updateId={0} keyResult={keyResult} />,
                                    }))}
                                >
                                    {lang.postUpdate}
                                </EmptyUpdateListTextButton>,
                            ])
                        }
                    </EmptyUpdateListWrapper>
                )
            }

            <UpdateListWrapper>
                {
                    updates.map((update) => { // displayed in reverse mode (column-reverse) - latest on top
                        const author = userList[update.author]
                        const createdAt = new Date(update.createdAt)
                        const dateUpdateDisplay = new Date(update.dateUpdate)
                        dateUpdateDisplay.setHours(createdAt.getHours(), createdAt.getMinutes())
                        const createdAtString = DateFormatterString(createdAt, lang)

                        let valueDisplay: string
                        if (keyResult.type === ACHIEVED) {
                            valueDisplay = update.value > 0 ? `"${lang.achieved}"` : `"${lang.notAchievedYet}"`
                        } else {
                            valueDisplay = `${metricBefore} ${update.value} ${metricAfter}`
                        }
                        let description = lang.userUpdatedTheValue
                        description = description.replace("{0}", `${author.firstName} ${author.lastName}`)
                        description = description.replace("{1}", valueDisplay)

                        const { diffProgress, progress } = update

                        return (
                            <UpdateWrapper key={update.id}>
                                <UpdateTitleWrapper>
                                    <UpdateTitle>
                                        <UserIcon user={author} size="sm" showName={false} />
                                        <DateFormatter date={dateUpdateDisplay} />
                                        {
                                            (
                                                dateUpdateDisplay.getDate() !== createdAt.getDate()
                                                || dateUpdateDisplay.getMonth() !== createdAt.getMonth()
                                                || dateUpdateDisplay.getFullYear() !== createdAt.getFullYear()
                                            ) && (
                                                <OverlayTrigger
                                                    trigger={["hover", "focus"]}
                                                    placement="top"
                                                    overlay={(
                                                        <TooltipStyled>
                                                            {lang.updatePostedOn.replace("{0}", createdAtString.date)}
                                                        </TooltipStyled>
                                                    )}
                                                >
                                                    <ClockWrapper><ClockIcon /></ClockWrapper>
                                                </OverlayTrigger>
                                            )
                                        }
                                    </UpdateTitle>
                                    <UpdateTitleConfidence>
                                        <Confidence confidenceLevel={update.confidenceLevel}>
                                            {lang[update.confidenceLevel]}
                                        </Confidence>
                                    </UpdateTitleConfidence>
                                    <UpdateTitlePercents>
                                        <ProgressPercents percents={progress}>{`${progress}%`}</ProgressPercents>
                                    </UpdateTitlePercents>
                                </UpdateTitleWrapper>

                                <UpdateBody>
                                    <UpdateBodyDescriptionWrapper>
                                        <UpdateBodyDescription>{description}</UpdateBodyDescription>
                                        <UpdateBodyPercents>
                                            <PercentsChange percents={diffProgress}>
                                                {`${diffProgress > 0 ? "+" : ""}${diffProgress}%`}
                                            </PercentsChange>
                                        </UpdateBodyPercents>
                                        <UpdateBodyOptions>
                                            {
                                                !isArchivedSession && (
                                                    <OptionsButton onClick={(e) => {
                                                        e.stopPropagation()
                                                        setShowPopup(true)
                                                        setPopupTarget(e.currentTarget as HTMLElement)
                                                        setActiveUpdate(update)
                                                    }}
                                                    >
                                                        <EllipsisIcon />
                                                    </OptionsButton>
                                                )
                                            }
                                        </UpdateBodyOptions>
                                    </UpdateBodyDescriptionWrapper>
                                    <UpdateBodyNote>
                                        <BookIcon />
                                        {update.note}
                                    </UpdateBodyNote>
                                </UpdateBody>
                            </UpdateWrapper>
                        )
                    })
                }
            </UpdateListWrapper>

            <PopupMenu
                show={showPopup}
                target={popupTarget}
                placement="bottom-end"
            >
                <PopupMenuItem onClick={onEditUpdate}>
                    {lang.edit}
                </PopupMenuItem>
                <PopupMenuItem onClick={() => setShowConfirmDelete(true)}>
                    {lang.delete}
                </PopupMenuItem>
            </PopupMenu>

            <ConfirmWrapper show={showConfirmDelete} onClose={onCancelDelete}>
                <Confirm>
                    <ConfirmTitle>{lang.deleteKeyResultUpdateConfirm}</ConfirmTitle>
                    <ConfirmBottom>
                        <Button variant="success" row onClick={onConfirmDelete}>
                            {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                        <Button row onClick={onCancelDelete}>{lang.cancel}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </KeyResultUpdateListWrapper>
    )
}
export default KeyResultUpdates
